import { ClockIcon, DocumentTextIcon, MailIcon, PhoneIcon, SparklesIcon, TagIcon, UserIcon, UsersIcon } from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import { HttpError, useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLodModal } from "components/historyLogModal";
import { SwitchButton } from "components/switchButton";
import { IOrgAccountDetail, IOrgEventIdsItem } from "interfaces";
import { useState } from "react";
import { Link } from "react-router-dom";
import { isHaveActionPermission } from "utills/helpers";

const EVENT_ALL_OPTION = {label: "All", value: "all"};

export const OrgAccountDetail: React.FC = () => {
  const [openLogModal, setOpenLogModal] = useState(false);
  const parse = useParse();
  const { params } = parse();

  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IOrgAccountDetail>({
    resource: "organization/account",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const orgAccountDetailData = data?.data ?? null;

  const { data: organizationEventsData } = useOne<IOrgEventIdsItem[], HttpError>({
    resource: "organization",
    id: `${orgAccountDetailData?.role.organization_id}/events`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!orgAccountDetailData?.role.organization_id,
    } as any,
  });

  const organizationEventIds = ((organizationEventsData?.data ?? []).length > 0 ? [EVENT_ALL_OPTION] : []).concat((organizationEventsData?.data ?? []).map((event: IOrgEventIdsItem) => (
    { value: event.id, label: event.name_en }
  )));

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Org's Account Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <Button
              className="bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200"
              onClick={() => setOpenLogModal(true)}
            >
              <span className="sr-only">History Log</span>
              <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">Log</span>
            </Button>
            {isHaveActionPermission(
              "account_management",
              "organization",
              "edit"
            ) ? (
              <Link
                to={`/organization/accounts/${orgAccountDetailData?.role.user_id}/edit`}
              >
                <Button>
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container p-0">
        <div className="divide-y divide-gray-100">
          <DescriptionItem 
            icon={TagIcon}
            label="Status"
            content={(
              <div className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {orgAccountDetailData?.role.is_active ? "Active" : "Inactive"}
              </div>
            )}
          />
          <DescriptionItem 
            icon={UsersIcon}
            label="Org name"
            content={(
              <p className="text-sm leading-6">
                {orgAccountDetailData?.role.organization_name}
              </p>
            )}
          />
          <DescriptionItem 
            icon={MailIcon}
            label="Email"
            content={(
              <p className="text-sm leading-6">
                {orgAccountDetailData?.role.email}
              </p>
            )}
          />
          <DescriptionItem 
            icon={UserIcon}
            label="Full Name"
            content={(
              <p className="text-sm leading-6">
                {orgAccountDetailData?.role.first_name} {orgAccountDetailData?.role.last_name}
              </p>
            )}
          />
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="Role name"
            content={(
              <p className="text-sm leading-6">
                {orgAccountDetailData?.role.role_name}
              </p>
            )}
          />

          <DescriptionItem 
            icon={PhoneIcon}
            label="Contact phone"
            content={(
              <p className="text-sm leading-6">
                {orgAccountDetailData?.role.phone ? (
                  <>
                    {orgAccountDetailData.role.phone_area_code ? `(+${orgAccountDetailData.role.phone_area_code})` : ""} {orgAccountDetailData.role.phone}
                  </>
                ): <></>}
              </p>
            )}
          />
          <DescriptionItem 
            icon={SparklesIcon}
            label="Event"
            content={organizationEventIds.map(event => orgAccountDetailData?.role.event_ids.includes(event.value) ? (
              <p className="text-sm leading-6" key={event.value}>{event.label}</p>
            ) : <></>)}
          />
        </div>
      </div>
      <HistoryLodModal
        resource={`organization/account/${params?.id}/history`}
        open={openLogModal}
        onClose={() => setOpenLogModal(false)}
        key="organization-account"
        id="organization-account"
      />
      
      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}